import React, {useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {
  addDoc,
  collection,
  doc,
  serverTimestamp,
  updateDoc,
  getDoc,
} from 'firebase/firestore'
import {useAuth} from '../../../../modules/auth'
import db from '../../../../../services/firebase'
import {useNavigate} from 'react-router-dom'
import {showSuccessToast} from 'app/components/ShowToast'
import {toAbsoluteUrl} from '_metronic/helpers'
import Dropzone from 'react-dropzone'
import Button from 'app/components/Button'
import {useAuditActions} from '../../hooks/useAuditActions'
import {AuditDocument} from '../../types/audit.types'
import Overlay from 'app/components/Overlay'

interface DeactivatingProps {
  isOpen: boolean
  onClose: () => void
  id: string
  onComplete: () => void
  audit
  selectedFileName?: {[key: string]: string}
}

const CompleteModal: React.FC<DeactivatingProps> = ({
  isOpen,
  onClose,
  id,
  audit,
  onComplete,
  selectedFileName = {},
}) => {
  const [showModal, setShowModal] = useState(false)
  const {currentUser} = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [formError, setFormError] = useState('')
  const [isUploading, setIsUploading] = useState(false)
  const [addAsTestEvidence, setAddAsTestEvidence] = useState(false)
  const navigate = useNavigate()

  const {handleFileChange, removeAuditDocument} = useAuditActions([], () => {})

  useEffect(() => {
    setShowModal(isOpen)
  }, [isOpen])

  const handleClose = () => {
    setShowModal(false)
    setFormError('')
    onClose()
  }

  const handleUpload = async (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0]
      setIsUploading(true)
      setFormError('')

      const syntheticEvent = {
        target: {
          files: [file],
        },
      } as unknown as React.ChangeEvent<HTMLInputElement>

      try {
        await handleFileChange(syntheticEvent, audit.id)
      } catch (error) {
        console.error('Upload error:', error)
        setFormError('Error uploading file. Please try again.')
      } finally {
        setIsUploading(false)
      }
    }
  }

  const handleDeleteFile = (auditId: string, document: AuditDocument) => {
    removeAuditDocument(auditId, document)
  }

  const currentDate = new Date()
  const year = currentDate.getFullYear()
  const month = String(currentDate.getMonth() + 1).padStart(2, '0')
  const day = String(currentDate.getDate()).padStart(2, '0')
  const formattedDate = `${year}-${month}-${day}`
  const todaysDate = `${month}/${day}/${year}`

  const addFileToTestEvidence = async (file: any) => {
    if (!file) return
    try {
      const testId = 'zZV7LmrelsDacLm2FIDT'
      const tenantId = currentUser?.tenantId || ''
      const testDocRef = doc(db, `tenants/${tenantId}/tests/${testId}`)
      const testDocSnapshot = await getDoc(testDocRef)
      let existingFiles = []
      if (testDocSnapshot.exists()) {
        existingFiles = testDocSnapshot.data().files || []
      }

      const now = new Date()
      const nextYear = new Date(now.getFullYear() + 1, now.getMonth(), now.getDate())
      const nextReviewDate = `${nextYear.getFullYear()}-${String(nextYear.getMonth() + 1).padStart(
        2,
        '0'
      )}-${String(nextYear.getDate()).padStart(2, '0')}`

      const fileEntry = {
        name: file.fileName,
        url: file.fileURL,
        date: new Date().toISOString(),
        size: file.fileSize,
        uploaded_at: serverTimestamp(),
        uploaded_by: currentUser?.userName ? currentUser?.userName.trim() : '',
        uploaded_uid: currentUser?.uid || '',
      }

      await updateDoc(testDocRef, {
        files: [...existingFiles, fileEntry],
        time_interval: 'Annually',
        timeframe: 'Annually',
        status: 'Pass',
        next_review_date: nextReviewDate,
        owner: currentUser?.userName ? currentUser?.userName.trim() : '',
      })
    } catch (err) {
      console.error('Error adding file to test evidence:', err)
    }
  }

  const handleMarkAsComplete = async (auditId, audit) => {
    setIsLoading(true)
    const tenantId = currentUser?.tenantId || ''
    const tenantRef2 = doc(db, 'tenants', tenantId)
    const policyRef = doc(tenantRef2, 'audits', auditId)

    const hasUploadedFile =
      (audit.audit_documents && audit.audit_documents.length > 0) || selectedFileName[auditId]

    if (!hasUploadedFile) {
      setFormError('Please upload an audit report before marking as complete')
      setIsLoading(false)
      return
    }

    await updateDoc(policyRef, {
      status: 'Completed',
      auditCompleteDate: formattedDate,
      end_date: formattedDate,
      TestEvidenceAdd: addAsTestEvidence,
    })

    if (addAsTestEvidence && audit.audit_documents && audit.audit_documents.length > 0) {
      await addFileToTestEvidence(audit.audit_documents[0])
    }

    if (audit.auditor && Array.isArray(audit.auditor) && audit.auditor.length > 0) {
      for (const auditor of audit.auditor) {
        if (auditor.id) {
          const employeeDocRef = doc(tenantRef2, 'employees', auditor.id)
          await updateDoc(employeeDocRef, {availability: true})
        }
      }
    }
    setIsLoading(false)

    const tenantRef = doc(db, 'tenants', tenantId)
    const activitiesRef = collection(tenantRef, 'activities')
    await addDoc(activitiesRef, {
      message: `The auditor ${currentUser?.userName} has marked the audit for the ${audit.framework[0]} framework as completed.`,
      timestamp: serverTimestamp(),
      tenantId: currentUser?.tenantId,
      uid: currentUser?.uid,
      username: currentUser?.userName,
      action: 'Audit updated',
      target: 'Audits',
      email: currentUser?.email,
      date: todaysDate,
    })
    navigate('/audits/completed')
    onClose()
    showSuccessToast({message: 'Audit marked as completed'})
  }

  const hasUploadedFile = audit?.audit_documents && audit.audit_documents.length > 0
  const uploadedDocument = hasUploadedFile ? audit.audit_documents[0] : null

  return (
    <Modal show={showModal} onHide={handleClose} backdropClassName='bg-opacity-1' centered>
      <Modal.Header closeButton>
        <Modal.Title>Complete Audit</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{padding: '20px'}}>
        {formError && (
          <div className='alert alert-danger' role='alert'>
            {formError}
          </div>
        )}
        <div className='fs-6' style={{color: '#111827'}}>
          Congratulations on completing the <span className='fw-semibold'>{audit?.auditTitle}</span>
          . To proceed, please upload a copy of your audit report.
        </div>
        <div className='fv-row mt-5'>
          <label className='fw-semibold fs-6 mb-4'>Audit Report</label>

          {hasUploadedFile && uploadedDocument ? (
            <>
              <div
                className='d-flex align-items-center justify-content-between'
                style={{
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  padding: '20px',
                }}
              >
                <div className='d-flex align-items-center'>
                  <div className=' me-5'>
                    <img
                      alt='file icon'
                      src={toAbsoluteUrl('/media/icon/File-bg.svg')}
                      style={{width: '35px'}}
                    />
                  </div>
                  <span className='fw-medium fs-6 text-dark'>{uploadedDocument.fileName}</span>
                </div>
                <button
                  className='btn btn-sm btn-icon btn-outline ms-2'
                  onClick={() => handleDeleteFile(audit.id, uploadedDocument)}
                  style={{flexShrink: 0}}
                  onMouseEnter={(e) => {
                    const svg = e.currentTarget.querySelector('svg')
                    if (svg) {
                      svg.style.stroke = 'oklch(57.7% 0.245 27.325)'
                    }
                  }}
                  onMouseLeave={(e) => {
                    const svg = e.currentTarget.querySelector('svg')
                    if (svg) {
                      svg.style.stroke = 'currentColor'
                    }
                  }}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='18'
                    height='18'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    className='icon icon-tabler icons-tabler-outline icon-tabler-trash'
                    style={{
                      transition: 'stroke 0.2s',
                    }}
                  >
                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                    <path d='M4 7l16 0' />
                    <path d='M10 11l0 6' />
                    <path d='M14 11l0 6' />
                    <path d='M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12' />
                    <path d='M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3' />
                  </svg>
                </button>
              </div>
              {currentUser?.role !== 'Auditor' && (
                <>
                  <p className='mt-4 fs-6 fw-semibold'>
                    Add as Evidence{' '}
                    <Overlay
                      content='This action will append the report to the corresponding test.'
                      placement='right'
                    />
                  </p>
                  <div className='form-check form-check-custom form-check-solid mt-2'>
                    <input
                      className='form-check-input'
                      type='checkbox'
                      name='addAsTestEvidence'
                      checked={addAsTestEvidence}
                      onChange={(e) => setAddAsTestEvidence(e.target.checked)}
                      disabled={currentUser?.role === 'Auditor'}
                    />
                    <label className='fw-semibold mx-4 fs-6'>
                      Include this as evidence in the test{' '}
                      <Overlay content='1. Internal Audit Report' placement='right' />
                    </label>
                  </div>
                </>
              )}
            </>
          ) : (
            <Dropzone
              onDrop={handleUpload}
              minSize={1024}
              accept={{
                'application/pdf': ['.pdf'],
                'application/msword': ['.doc'],
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
                  '.docx',
                ],
              }}
              maxSize={10 * 1024 * 1024}
              disabled={isUploading}
            >
              {({getRootProps, getInputProps}) => (
                <div
                  {...getRootProps({
                    className: 'dropzone',
                    style: {
                      backgroundColor: 'white',
                      border: '2px dashed #E5E7EB',
                      borderRadius: '8px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '24px 32px',
                      textAlign: 'center',
                      cursor: isUploading ? 'not-allowed' : 'pointer',
                      opacity: isUploading ? 0.6 : 1,
                    },
                  })}
                >
                  <input {...getInputProps()} />
                  <div className='d-flex flex-column align-items-center' style={{gap: '16px'}}>
                    <div className='d-flex flex-column align-items-center' style={{gap: '7px'}}>
                      <div
                        className='d-flex align-items-center justify-content-center'
                        style={{
                          backgroundColor: '#F3F4F6',
                          borderRadius: '12px',
                          height: '48px',
                          width: '48px',
                        }}
                      >
                        <img
                          alt='placeholder icon'
                          src={toAbsoluteUrl('/media/icon/image-placeholder.svg')}
                          className=''
                        />
                      </div>

                      <span className='fs-6 fw-semibold' style={{color: '#111827'}}>
                        Drag and drop file here
                      </span>

                      <span className='fw-normal' style={{fontSize: '12px', color: '#111827'}}>
                        Supported files: pdf, doc, docx. Max 10 MB.
                      </span>
                    </div>
                    <div>
                      <Button>
                        {isUploading ? (
                          <span className='fs-7 fw-medium'>
                            Uploading...{' '}
                            <span className='spinner-border spinner-border-sm me-2'></span>
                          </span>
                        ) : (
                          <>
                            <img
                              alt='upload-icon'
                              src={toAbsoluteUrl('/media/icon/upload.svg')}
                              className='me-2'
                              style={{height: '18px', width: '18px'}}
                            />
                            <span className='text-dark fs-6 fw-medium'>Upload File</span>
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </Dropzone>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer className='p-0 px-6 py-4 gap-2'>
        {isLoading ? (
          <button
            className='btn btn-sm fs-6'
            disabled
            style={{
              backgroundColor: '#0E9F6E',
              color: '#fff',
              height: '40px',
              borderRadius: '4px',
              transition: 'background-color 0.3s',
            }}
          >
            Updating... <span className='spinner-border spinner-border-sm align-middle'></span>
          </button>
        ) : (
          <button
            className='btn btn-sm fs-6'
            onClick={() => handleMarkAsComplete(audit.id, audit)}
            style={{
              backgroundColor: '#0E9F6E',
              color: '#fff',
              height: '40px',
              borderRadius: '4px',
              transition: 'background-color 0.3s',
            }}
            disabled={!(audit?.audit_documents && audit.audit_documents.length > 0)}
          >
            Complete Audit
          </button>
        )}
      </Modal.Footer>
    </Modal>
  )
}

export default CompleteModal
